import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, Sparkles, FolderKanban, Boxes, Building2, Inbox, FileText, Truck, Wrench, AlertTriangle, ListChecks, Map as MapIcon, CalendarRange, ArrowUpRight, Copy } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Widget } from "./Widgets";
import { ActionCard } from "./ActionCard";

const SRC_ICON = { project: FolderKanban, module: Boxes, customer: Building2, lead: Inbox, quote: FileText, transport: Truck, service: Wrench, issue: AlertTriangle, task: ListChecks, map: MapIcon, planning: CalendarRange };

const md = {
  p: ({ children }) => <p className="my-1.5 leading-6">{children}</p>,
  ul: ({ children }) => <ul className="my-1.5 list-disc space-y-0.5 pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="my-1.5 list-decimal space-y-0.5 pl-5">{children}</ol>,
  li: ({ children }) => <li className="leading-6">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  h1: ({ children }) => <h3 className="mt-3 text-[13.5px] font-semibold">{children}</h3>,
  h2: ({ children }) => <h3 className="mt-3 text-[13.5px] font-semibold">{children}</h3>,
  h3: ({ children }) => <h3 className="mt-3 text-[13.5px] font-semibold">{children}</h3>,
  a: ({ href, children }) => (
    <a href={href} className="text-primary underline-offset-2 hover:underline" target={href?.startsWith("/") ? undefined : "_blank"} rel="noreferrer">
      {children}
    </a>
  ),
  code: ({ children }) => <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11.5px]">{children}</code>,
  table: ({ children }) => <table className="my-2 w-full text-[12px]">{children}</table>,
  th: ({ children }) => <th className="px-2 py-1 text-left font-medium text-muted-foreground">{children}</th>,
  td: ({ children }) => <td className="border-t border-border/60 px-2 py-1">{children}</td>,
};

/** Live progress while tools run: «Sjekker ledige moduler … ✓ total 239». */
export const Progress = ({ progress }) => {
  if (!progress) return null;
  return (
    <div className="space-y-1 text-[12px] text-muted-foreground" data-testid="ai-progress" aria-live="polite">
      {progress.tools?.map((t) => (
        <div key={t.id} className="flex items-center gap-2">
          {t.done ? <Check className={cn("h-3.5 w-3.5", t.ok ? "text-emerald-600" : "text-amber-600")} strokeWidth={2} /> : <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />}
          <span className={cn(t.done && "text-foreground/70")}>{t.label}</span>
          {t.done && t.summary && <span className="truncate text-muted-foreground/70">· {t.summary}</span>}
        </div>
      ))}
      {(!progress.tools?.length || progress.stage === "answering") && (
        <div className="flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" /> {progress.stage === "answering" ? "Formulerer svar …" : "Tenker …"}
        </div>
      )}
    </div>
  );
};

export const Sources = ({ sources }) => {
  const navigate = useNavigate();
  if (!sources?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="ai-sources">
      {sources.map((s) => {
        const Icon = SRC_ICON[s.type] || FileText;
        return (
          <button key={`${s.type}-${s.id}`} type="button" onClick={() => s.url && navigate(s.url)} title={s.sub} className="inline-flex max-w-[240px] items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-[11.5px] leading-4 transition-colors duration-150 hover:bg-primary/10 hover:text-primary" data-testid={`ai-source-${s.type}`}>
            <Icon className="h-3 w-3 shrink-0 opacity-70" strokeWidth={1.75} />
            <span className="truncate">{s.label}</span>
            {s.sub && <span className="hidden truncate text-muted-foreground sm:inline">· {s.sub}</span>}
          </button>
        );
      })}
    </div>
  );
};

export const UserBubble = ({ m }) => (
  <div className="flex justify-end" data-testid="ai-message-user">
    <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-foreground px-3.5 py-2 text-[13px] leading-5 text-background">{m.content}</div>
  </div>
);

export const AssistantMessage = ({ m, progress, dense, onFollowup }) => {
  const parts = splitNext(m.content || "");
  const copy = () => {
    navigator.clipboard?.writeText(m.content || "");
    toast.success("Kopiert");
  };
  return (
    <div className="group/msg flex gap-3" data-testid="ai-message-assistant">
      <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary" aria-hidden>
        <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} />
      </span>
      <div className="min-w-0 flex-1 space-y-3">
        {!m.done && !m.content && <Progress progress={progress} />}
        {m.content && (
          <div className={cn("text-[13.5px] text-foreground/90", m.error && "text-rose-700 dark:text-rose-300")} data-testid="ai-answer">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={md}>
              {parts.body}
            </ReactMarkdown>
            {!m.done && <span className="ml-0.5 inline-block h-4 w-[2px] animate-pulse rounded bg-primary align-text-bottom" aria-hidden />}
          </div>
        )}
        {!m.done && m.content && progress?.tools?.some((t) => !t.done) && <Progress progress={progress} />}
        {m.widgets?.length > 0 && (
          <div className="space-y-3" data-testid="ai-widgets">
            {m.widgets.map((w, i) => (
              <Widget key={i} w={w} dense={dense} />
            ))}
          </div>
        )}
        {m.actions?.length > 0 && (
          <div className="space-y-2">
            {m.actions.map((a) => (
              <ActionCard key={a.id} action={a} />
            ))}
          </div>
        )}
        {m.done && parts.next?.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="ai-followups">
            {parts.next.map((q, i) => (
              <button key={i} type="button" onClick={() => onFollowup?.(q)} className="inline-flex items-center gap-1 rounded-full bg-card px-3 py-1 text-[12px] ring-1 ring-black/[0.06] transition-colors duration-150 hover:bg-primary/10 hover:text-primary hover:ring-primary/20 dark:ring-white/10" data-testid="ai-followup">
                {q} <ArrowUpRight className="h-3 w-3 opacity-60" />
              </button>
            ))}
          </div>
        )}
        {m.done && (m.sources?.length > 0 || m.content) && (
          <div className="flex flex-wrap items-center gap-2">
            <Sources sources={m.sources} />
            {m.content && (
              <button type="button" onClick={copy} className="ml-auto inline-flex h-6 items-center gap-1 rounded-md px-1.5 text-[11px] text-muted-foreground opacity-0 transition-opacity duration-150 hover:bg-muted hover:text-foreground group-hover/msg:opacity-100 focus-visible:opacity-100" aria-label="Kopier svar" data-testid="ai-copy">
                <Copy className="h-3 w-3" /> Kopier
              </button>
            )}
          </div>
        )}
        {m.done && m.tools_used?.length > 0 && (
          <div className="text-[10.5px] text-muted-foreground/70" data-testid="ai-tools-used">
            Grunnlag: {m.tools_used.map((t) => t.label || t.name).join(" · ")}
          </div>
        )}
      </div>
    </div>
  );
};

/** The model ends with «Neste: …» – lift those into chips instead of prose. */
function splitNext(text) {
  const idx = text.lastIndexOf("Neste:");
  if (idx === -1) return { body: text, next: [] };
  const body = text.slice(0, idx).trimEnd();
  const tail = text.slice(idx + 6).trim();
  const next = tail
    .split(/\n|(?<=\?)\s+(?=[A-ZÆØÅ])/)
    .map((s) => s.replace(/^[-•*\d.)\s]+/, "").replace(/\*\*|`/g, "").trim())
    .filter((s) => s.length > 8)
    .slice(0, 3);
  return { body, next };
}
